import React, { createContext, useState, useContext, ReactNode } from 'react';
import { Snackbar, Alert, AlertColor } from '@mui/material';

interface SnackbarContextType {
    showSnackbar: (message: string, severity: AlertColor, persistent?: boolean) => void;
    hideSnackbar: () => void;
}

interface SnackbarMessage {
    message: string;
    severity: AlertColor;
    persistent?: boolean;
}

interface SnackbarProviderProps {
    children: ReactNode;
}

const SnackbarContext = createContext<SnackbarContextType | null>(null);

export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({ children }) => {
    const [open, setOpen] = useState(false);
    const [snackbar, setSnackbar] = useState<SnackbarMessage | null>(null);

    const showSnackbar = (message: string, severity: AlertColor, persistent = false) => {
        setSnackbar({ message, severity, persistent });
        setOpen(true);
    };

    const hideSnackbar = () => {
        setOpen(false);
    };

    const handleClose = () => {
        setOpen(false);
    };

    return (
        <SnackbarContext.Provider value={{ showSnackbar, hideSnackbar }}>
            {children}
            {snackbar && (
                <Snackbar
                    open={open}
                    autoHideDuration={snackbar.persistent ? null : 6000}
                    onClose={handleClose}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Alert onClose={handleClose} severity={snackbar.severity} sx={{ width: '100%', boxShadow: 1 }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            )}
        </SnackbarContext.Provider>
    );
};

export const useSnackbar = (): SnackbarContextType => {
    const context = useContext(SnackbarContext);

    if (!context) {
        throw new Error('useSnackbar must be used within a SnackbarProvider');
    }

    return context;
};

export default SnackbarContext;
